import { NumericFormat } from 'react-number-format'
import { centsToDollars, dollarsToCents } from '~/shared/lib/currency'

interface CurrencyCellProps {
  /** Value in cents (integer) when dollars, raw number when percentage. */
  value: number | null
  onCentsChange: (cents: number | null) => void
  onBlur?: React.FocusEventHandler<HTMLInputElement>
  disabled?: boolean
  /** When true, renders a plain numeric input (no currency conversion, capped at 100). */
  isPercentage?: boolean
}

/**
 * Compact currency/percentage cell for the service lines table.
 * In dollar mode: stores cents, displays dollars via centsToDollars/dollarsToCents.
 * In percentage mode: stores raw number, no conversion, no fixed decimals.
 */
export function CurrencyCell({
  value,
  onCentsChange,
  onBlur,
  disabled,
  isPercentage,
}: CurrencyCellProps) {
  if (isPercentage) {
    return (
      <NumericFormat
        className="text-text-primary disabled:text-text-secondary w-full border-none bg-transparent px-0 py-1 text-[15px] leading-5 tracking-[0.25px] outline-none"
        disabled={disabled}
        value={value ?? ''}
        decimalScale={2}
        allowNegative={false}
        isAllowed={({ floatValue }) => floatValue === undefined || floatValue >= 0}
        onValueChange={(values) => onCentsChange(values.floatValue ?? null)}
        onBlur={onBlur}
      />
    )
  }

  return (
    <NumericFormat
      className="text-text-primary disabled:text-text-secondary w-full border-none bg-transparent px-0 py-1 text-[15px] leading-5 tracking-[0.25px] outline-none"
      disabled={disabled}
      value={centsToDollars(value) ?? ''}
      thousandSeparator
      decimalScale={2}
      fixedDecimalScale
      allowNegative
      allowedDecimalSeparators={[',', '.']}
      onValueChange={(values) => onCentsChange(dollarsToCents(values.floatValue ?? null))}
      onBlur={onBlur}
    />
  )
}
